import { useAuthentication } from '@givebacks/api';
import { useBreakPoints } from 'core';
import {
  AspectRatio,
  Badge,
  ButtonMantine,
  Card,
  Container,
  Grid,
  Group,
  ImageMantine,
  List,
  MobileBottomButton,
  Stack,
  Text,
  Title,
} from 'ui';
import { IconCircleCheck } from 'ui/Icons';

import { useNavigateToAuth } from '../../../hooks/useNavigateToAuth/useNavigateToAuth';

type WaysToGiveBackLayoutProps = {
  mediaSrc?: string;
  title: string;
  overline?: string;
  cta?: { label: string; onClick: () => void };
  tagline: string;
  copy: React.ReactNode;
  howItWorks?: { listItems?: string[]; content?: React.ReactNode };
  successMessage?: string;
};

const CTAButton = ({
  cta,
  isAuthenticated,
}: {
  cta?: { label: string; onClick: () => void };
  isAuthenticated: boolean;
}) => {
  const { isSmallScreen } = useBreakPoints();
  const { handleNavigateToAuth } = useNavigateToAuth();
  const getStarted = () => {
    handleNavigateToAuth({ page: 'sign-up' });
  };

  if (!cta) return null;

  return (
    <ButtonMantine
      fullWidth={isSmallScreen ? true : false}
      onClick={isAuthenticated ? cta?.onClick : getStarted}
      variant={isAuthenticated ? 'filled' : 'gradient'}
      size={isSmallScreen ? 'lg' : 'md'}
    >
      {isAuthenticated ? cta?.label : 'Create an Account'}
    </ButtonMantine>
  );
};

const Header = ({
  title,
  overline = 'Giveback when you',
  cta,
}: {
  title: string;
  overline?: string;
  cta?: { label: string; onClick: () => void };
}) => {
  const { isAuthenticated } = useAuthentication();
  const { isSmallScreen } = useBreakPoints();
  return (
    <Group gap="lg" justify="space-between">
      <Stack gap={0}>
        <Text type="sub" tt="uppercase" fw={500} c="cobalt" id="growthbook-home-overline">
          {overline}
        </Text>
        <Title order={1} id="growthbook-home-title">
          {title}
        </Title>
      </Stack>
      {cta && !isSmallScreen && <CTAButton cta={cta} isAuthenticated={isAuthenticated} />}
    </Group>
  );
};

const HowItWorks = ({
  content,
  listItems,
}: {
  content?: React.ReactNode;
  listItems?: string[];
}) => {
  return (
    <Card bg="cobalt.0" radius="sm" p="lg">
      <Stack gap="lg" id="growthbook-home-how-it-works">
        <Stack gap={4}>
          <Title order={4}>How it works:</Title>
          <List type="ordered" c="gray" fz={{ base: 'xs', md: 'sm' }}>
            {listItems?.map((item, index) => (
              <List.Item key={index}>{`${index + 1}. ${item}`}</List.Item>
            ))}
          </List>
        </Stack>
        {content}
      </Stack>
    </Card>
  );
};

const Content = ({
  title,
  overline,
  cta,
  tagline,
  copy,
  howItWorks,
  successMessage,
}: Partial<WaysToGiveBackLayoutProps>) => {
  const { isXSmallScreen, isMediumScreen } = useBreakPoints();

  return (
    <Stack gap={isXSmallScreen ? 'md' : 'lg'}>
      <Stack>
        {!isMediumScreen && title && <Header title={title} overline={overline} cta={cta} />}
        <Stack gap="xs">
          {successMessage && (
            <Badge type="success" variant="filled" leftSection={<IconCircleCheck size={12} />}>
              {successMessage}
            </Badge>
          )}
          <Text type="body" id="growthbook-home-tagline">
            {tagline}
          </Text>
          <Text type="body" id="growthbook-home-copy">
            {copy}
          </Text>
        </Stack>
      </Stack>

      <HowItWorks content={howItWorks?.content} listItems={howItWorks?.listItems} />
    </Stack>
  );
};

const WaysToGiveBackLayout = ({
  mediaSrc,
  title,
  overline,
  cta,
  tagline,
  copy,
  howItWorks,
  successMessage,
}: WaysToGiveBackLayoutProps) => {
  const { isSmallScreen, isMediumScreen } = useBreakPoints();
  const { isAuthenticated } = useAuthentication();

  return (
    <Container size="xl" px={{ base: 'sm', xl: 0 }} mb={isSmallScreen ? 80 : undefined}>
      <Grid gutter={{ base: 'md', sm: 'lg', md: 'xl' }}>
        <Grid.Col span={{ base: 12, md: 5 }}>
          <Stack gap={isSmallScreen ? 'md' : 'lg'}>
            {isMediumScreen && <Header title={title} overline={overline} cta={cta} />}

            <AspectRatio ratio={16 / 9}>
              {/* TODO: (Home) Update Ways to Giveback pages video @acdodd17*/}
              <ImageMantine src={mediaSrc} radius="lg" />
            </AspectRatio>
          </Stack>
        </Grid.Col>
        <Grid.Col span={{ base: 12, md: 6 }} offset={{ base: 0, md: 1 }}>
          <Content
            title={title}
            overline={overline}
            cta={cta}
            tagline={tagline}
            copy={copy}
            howItWorks={howItWorks}
            successMessage={successMessage}
          />
        </Grid.Col>
      </Grid>
      {isSmallScreen && (
        <MobileBottomButton>
          <CTAButton cta={cta} isAuthenticated={isAuthenticated} />
        </MobileBottomButton>
      )}
    </Container>
  );
};

export { WaysToGiveBackLayout };
